import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import images from "../../assets/images";
import OwlCarousel from "../../components/carousel";
import TemplateLayout from "../../components/layouts/templatelayout";
import Loader from "../../components/loader";
import { useTranslate } from "../../context/TranslationContext";
import { useGetCategoriesQuery } from "../../redux/Apis/Category";
import { useGetContentQuery } from "../../redux/Apis/Content";
import { useGetAllTemplatesQuery } from "../../redux/Apis/Template";
import "./style.css";

const Template = () => {
  const navigate = useNavigate();
  const { t } = useTranslate("templateList");
  const lang = useSelector((state) => state.language.lang);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { data: featuredVideos, isLoading: isFeaturedVideosLoading } =
    useGetContentQuery({
      search: "featured-videos",
      lang,
    });

  const templateVideoUrl =
    featuredVideos?.data?.sections?.find(
      (s) => s.title === "Featured Template Video",
    )?.content || "";

  const getEmbedUrl = (url, autoplay = true) => {
    if (!url) return null;

    try {
      const urlObj = new URL(url);

      if (
        !urlObj.hostname.includes("youtube.com") &&
        !urlObj.hostname.includes("youtu.be")
      ) {
        return null;
      }

      const videoId = urlObj.searchParams.get("v");
      const listId = urlObj.searchParams.get("list");

      if (listId) {
        return `https://www.youtube.com/embed/videoseries?list=${listId}${
          autoplay ? "&autoplay=1&mute=1" : ""
        }`;
      }

      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}${
          autoplay ? "?autoplay=1&mute=1" : ""
        }`;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const embedUrl = getEmbedUrl(templateVideoUrl, !isMobile);

  const { data: categoryData, isLoading: isCategoryLoading } =
    useGetCategoriesQuery({ lang });

  const categories = categoryData?.data || [];

  const { data: templateData, isLoading: isTemplateLoading } =
    useGetAllTemplatesQuery(
      { status: "approved", lang },
      { refetchOnMountOrArgChange: true },
    );

  const templates = useMemo(() => templateData?.data || [], [templateData]);

  const groupedTemplates = useMemo(() => {
    const groups = {};
    templates.forEach((tpl) => {
      const catId = tpl.category?._id;
      if (!groups[catId]) groups[catId] = [];
      groups[catId].push(tpl);
    });
    return groups;
  }, [templates]);

  if (isTemplateLoading || isCategoryLoading || isFeaturedVideosLoading) {
    return (
      <>
        <TemplateLayout />
        <Loader loading={true} />
      </>
    );
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>
          Fitness Templates | Ready-to-Use Training Resources | Traineego
        </title>

        <meta
          name="description"
          content="Access professionally designed fitness templates, workout plans, coaching resources, and productivity tools."
        />

        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://traineego.com/template" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Fitness Template",
            url: "https://traineego.com/template",
            description:
              "Access professionally designed fitness templates, workout plans, coaching resources, and productivity tools.",
            publisher: {
              "@type": "Organization",
              name: "Traineego",
              url: "https://traineego.com",
            },
          })}
        </script>
      </Helmet>
      <TemplateLayout>
        <section
          style={{
            backgroundColor: "#000",
            color: "#fff",
            padding: "40px 0",
            minHeight: "100vh",
          }}
        >
          {embedUrl && (
            <div className="container mb-5">
              <div className="video-container ratio ratio-16x9 mb-5">
                <iframe
                  src={embedUrl}
                  title="Featured Template Video"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  style={{ borderRadius: "12px" }}
                />
              </div>
            </div>
          )}

          <div className="sticky-header-wrapper mb-5">
            <div className="container my-2">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                <p
                  className="text-white fw-bold mb-0"
                  style={{ fontSize: "28px", flexGrow: 1 }}
                >
                  {t("title")}
                </p>

                <div style={{ width: "100%", maxWidth: "280px" }}>
                  <Select
                    isClearable
                    placeholder={t("select")}
                    value={
                      selectedCategory
                        ? {
                            value: selectedCategory,
                            label:
                              categories.find((c) => c._id === selectedCategory)
                                ?.name || "Unknown",
                          }
                        : null
                    }
                    onChange={(option) =>
                      setSelectedCategory(option ? option.value : "")
                    }
                    options={categories.map((c) => ({
                      value: c._id,
                      label: c.name,
                    }))}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "6px",
                        minHeight: "38px",
                        fontSize: "14px",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      option: (base, { isFocused, isSelected }) => ({
                        ...base,
                        backgroundColor: isSelected
                          ? "var(--primaryColor)"
                          : isFocused
                            ? "#e9ecef"
                            : "#f8f9fa",
                        color: isSelected ? "#ffffff" : "#000000",
                        cursor: "pointer",
                        ":active": {
                          ...base[":active"],
                          backgroundColor: "#0056b3",
                        },
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="container">
            {selectedCategory
              ? (() => {
                  const selectedCat = categories.find(
                    (c) => c._id === selectedCategory,
                  );
                  const catTemplates = groupedTemplates[selectedCategory] || [];

                  if (!catTemplates.length) {
                    return (
                      <div className="text-center text-muted py-5">
                        <h5>
                          {t("notFound")} {selectedCat?.name || t("thisCat")}.
                        </h5>
                      </div>
                    );
                  }

                  return (
                    <section className="mb-5" key={selectedCategory}>
                      <div className="row mb-1">
                        <div className="col-12 d-flex align-items-end gap-2 flex-wrap">
                          <h4 className="text-uppercase mb-0 me-2">
                            {selectedCat?.name}
                          </h4>
                          <p className="mb-0 m-purple-text">
                            {catTemplates.length}{" "}
                            {catTemplates.length > 1
                              ? t("templates")
                              : t("template")}
                          </p>
                        </div>
                      </div>

                      <OwlCarousel
                        className="category-carousel"
                        options={{
                          margin: 10,
                          loop: false,
                          nav: true,
                          dots: false,
                          lazyLoad: true,
                          autoplay: true,
                          autoplayTimeout: 5000,
                          autoplayHoverPause: true,
                          smartSpeed: 500,
                          navText: [
                            '<i class="fas fa-angle-left"></i>',
                            '<i class="fas fa-angle-right"></i>',
                          ],
                          responsive: {
                            0: { items: 2 },
                            576: { items: 2 },
                            768: { items: 3 },
                            992: { items: 4 },
                            1200: { items: 5 },
                          },
                        }}
                      >
                        {catTemplates.map((tpl) => (
                          <div key={tpl._id} className="template-card-wrapper">
                            <div className="template-card">
                              <img
                                src={
                                  tpl?.file?.startsWith("http")
                                    ? tpl.file
                                    : images.dummy.course
                                }
                                alt={tpl.title_hook || tpl.title}
                                className="template-card-img"
                              />
                              <div className="p-3 d-flex flex-column flex-grow-1">
                                <h6 className="fw-bold">
                                  {tpl.title_hook || tpl.title}
                                </h6>
                                <p
                                  style={{ fontSize: "13px", color: "#ccc" }}
                                  dangerouslySetInnerHTML={{
                                    __html: tpl.subtitle || "",
                                  }}
                                />
                                <div className="d-flex flex-wrap gap-1 my-2">
                                  {tpl.audience &&
                                    (() => {
                                      const audienceParts = tpl.audience
                                        .replace(/<\/?[^>]+(>|$)/g, "")
                                        .split(/\s*(?:\/|&|,|and|-)\s*/i)
                                        .filter(Boolean);

                                      const maxItems = 10; // roughly 3 lines depending on width
                                      const showEllipsis =
                                        audienceParts.length > maxItems;

                                      return (
                                        <div className="d-flex flex-wrap gap-1 my-2">
                                          {audienceParts
                                            .slice(0, maxItems)
                                            .map((part, idx) => (
                                              <div
                                                key={idx}
                                                className="template-audience"
                                              >
                                                {part.trim()}
                                              </div>
                                            ))}
                                          {showEllipsis && (
                                            <div className="template-audience">
                                              ...
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                </div>

                                <p style={{ fontSize: "14px", color: "#888" }}>
                                  {tpl.access_type === "paid"
                                    ? t("full")
                                    : tpl.access_type === "free"
                                      ? t("free")
                                      : t("Membership")}
                                </p>
                                <div className="mt-auto">
                                  <button
                                    className="site-btn mt-2"
                                    style={{ minWidth: "fit-content" }}
                                    onClick={() =>
                                      navigate(`/templates/${tpl._id}`)
                                    }
                                  >
                                    {t("view")}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </OwlCarousel>
                    </section>
                  );
                })()
              : categories.map((cat) => {
                  const catTemplates = groupedTemplates[cat._id] || [];
                  if (!catTemplates.length) return null;

                  return (
                    <section className="mb-5" key={cat._id}>
                      <div className="row mb-1">
                        <div className="col-12 d-flex align-items-end gap-2 flex-wrap">
                          <h4 className="text-uppercase mb-0 me-2">
                            {cat.name}
                          </h4>
                          <p className="mb-0 m-purple-text">
                            {catTemplates.length}{" "}
                            {catTemplates.length > 1
                              ? t("templates")
                              : t("template")}
                          </p>
                        </div>
                      </div>

                      <OwlCarousel
                        className="category-carousel"
                        options={{
                          margin: 10,
                          loop: false,
                          nav: true,
                          dots: false,
                          lazyLoad: true,
                          autoplay: true,
                          autoplayTimeout: 5000,
                          autoplayHoverPause: true,
                          smartSpeed: 500,
                          navText: [
                            '<i class="fas fa-angle-left"></i>',
                            '<i class="fas fa-angle-right"></i>',
                          ],
                          responsive: {
                            0: { items: 2 },
                            576: { items: 2 },
                            768: { items: 3 },
                            992: { items: 4 },
                            1200: { items: 5 },
                          },
                        }}
                      >
                        {catTemplates.map((tpl) => (
                          <div key={tpl._id} className="template-card-wrapper">
                            <div className="template-card">
                              <img
                                src={
                                  tpl?.file?.startsWith("http")
                                    ? tpl.file
                                    : images.dummy.course
                                }
                                alt={tpl.title_hook || tpl.title}
                                className="template-card-img"
                              />
                              <div className="p-3 d-flex flex-column flex-grow-1">
                                <h6 className="fw-bold">
                                  {tpl.title_hook || tpl.title}
                                </h6>
                                <p
                                  style={{ fontSize: "13px", color: "#ccc" }}
                                  dangerouslySetInnerHTML={{
                                    __html: tpl.subtitle || "",
                                  }}
                                />
                                <div className="d-flex flex-wrap gap-1 my-2">
                                  {tpl.audience &&
                                    tpl.audience
                                      .replace(/<\/?[^>]+(>|$)/g, "")
                                      .split(/\s*(?:\/|&|,|and|-)\s*/i)
                                      .filter(Boolean)
                                      .map((part, idx) => (
                                        <div
                                          key={idx}
                                          className="template-audience"
                                        >
                                          {part.trim()}
                                        </div>
                                      ))}
                                </div>

                                <p style={{ fontSize: "14px", color: "#888" }}>
                                  {tpl.access_type === "paid"
                                    ? t("full")
                                    : tpl.access_type === "free"
                                      ? t("free")
                                      : t("Membership")}
                                </p>
                                <div className="mt-auto">
                                  <button
                                    className="site-btn mt-2"
                                    style={{ minWidth: "fit-content" }}
                                    onClick={() =>
                                      navigate(`/templates/${tpl._id}`)
                                    }
                                  >
                                    {t("view")}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </OwlCarousel>
                    </section>
                  );
                })}
          </div>
        </section>
      </TemplateLayout>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="btn purple-text rounded-circle shadow"
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 1050,
            width: "50px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="bi bi-arrow-up-circle-fill fs-3"></i>
        </button>
      )}
    </>
  );
};

export default Template;
